import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, compoundGrowthByYear } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function RetirementPage() {
    const [age, setAge] = useState(32);
    const [retireAge, setRetireAge] = useState(65);
    const [current, setCurrent] = useState(45000);
    const [salary, setSalary] = useState(85000);
    const [contribPct, setContribPct] = useState(10);
    const [matchPct, setMatchPct] = useState(4);
    const [returnRate, setReturnRate] = useState(7);

    const result = useMemo(() => {
        const years = Math.max(0, toNum(retireAge) - toNum(age));
        const monthly = (toNum(salary) * (toNum(contribPct) + toNum(matchPct))) / 100 / 12;
        const series = compoundGrowthByYear(toNum(current), monthly, toNum(returnRate), years);
        const fv = series[series.length - 1]?.balance ?? toNum(current);
        const annualIncome = fv * 0.04;
        return { years, monthly, fv, annualIncome, series };
    }, [age, retireAge, current, salary, contribPct, matchPct, returnRate]);

    return (
        <CalculatorShell
            slug="retirement"
            explainer={
                               <>
                    <h2>How this retirement calculator works</h2>
                    <p>
                        A retirement calculator projects your 401(k) or retirement account balance at your
                        target retirement age, given your current savings, ongoing contributions, employer
                        match, and expected return rate. Use it to see if you're on track or how much more
                        you need to save.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Current balance:</strong> grows at your expected return compounded monthly over the remaining years to retirement.</li>
                        <li><strong>Monthly contributions:</strong> added each period and each one compounds forward from the month it's made.</li>
                        <li><strong>Employer match:</strong> added as a percentage of your contribution up to the match limit — essentially free money.</li>
                        <li><strong>Expected return:</strong> 5–8% is commonly used for diversified portfolios; adjust to reflect your actual allocation.</li>
                        <li><strong>Projected balance:</strong> totals compounded growth from your starting balance, contributions, and employer match.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Always contribute at least enough to capture the full employer match — it's an immediate 50–100% return.</li>
                        <li>Increase your contribution rate by 1% each year, ideally timed with a raise so you don't feel it.</li>
                        <li>Model retiring at 62 vs. 67 to see how five extra working years affect your projected balance.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Current age" value={age} onChange={setAge} dataTestId="calc-input-age" />
                        <Field label="Retirement age" value={retireAge} onChange={setRetireAge} dataTestId="calc-input-retire-age" />
                    </div>
                    <Field label="Current balance" prefix="$" value={current} onChange={setCurrent} dataTestId="calc-input-current" />
                    <Field label="Annual salary" prefix="$" value={salary} onChange={setSalary} dataTestId="calc-input-salary" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Your contribution" suffix="%" value={contribPct} onChange={setContribPct} step={0.5} dataTestId="calc-input-contrib" />
                        <Field label="Employer match" suffix="%" value={matchPct} onChange={setMatchPct} step={0.5} dataTestId="calc-input-match" />
                    </div>
                    <Field label="Expected annual return" suffix="%" value={returnRate} onChange={setReturnRate} step={0.25} dataTestId="calc-input-return" />
                </div>
                <div className="lg:col-span-2 space-y-4">
                    <ResultStat
                        label="Nest egg at retirement"
                        value={result.fv}
                        format={(n) => fmtUSD(n, { decimals: 0 })}
                        secondary={[
                            { label: "Years to grow", value: result.years, format: (n) => `${n}`, testId: "calc-result-years" },
                            { label: "Monthly contrib", value: result.monthly, format: (n) => fmtUSD(n), testId: "calc-result-monthly" },
                        ]}
                    />
                    <div className="bg-card border border-border rounded-2xl p-6">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                            Potential annual income (4% rule)
                        </div>
                        <div className="font-mono tabular text-3xl text-primary mt-2" data-testid="calc-result-income">
                            {fmtUSD(result.annualIncome, { decimals: 0 })}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                            ≈ {fmtUSD(result.annualIncome / 12, { decimals: 0 })}/month
                        </div>
                    </div>
                </div>
            </div>
            <div className="mt-10 bg-card border border-border rounded-xl p-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-accent">Projection</div>
                <h3 className="font-serif text-2xl text-primary mt-1 mb-4">Balance by age</h3>
                <figure aria-label={`Retirement balance projection from age ${toNum(age)} to ${toNum(retireAge)}, growing to ${fmtUSD(result.fv, { decimals: 0 })}`}>
                    <div className="h-72 w-full" data-testid="calc-chart" style={{ minHeight: 288 }}>
                    <ResponsiveContainer width="100%" height="100%" minHeight={288}>
                        <AreaChart data={result.series.map((s) => ({ ...s, age: toNum(age) + s.year }))}>
                            <defs>
                                <linearGradient id="gRet" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="hsl(150 55% 9%)" stopOpacity={0.35} />
                                    <stop offset="95%" stopColor="hsl(150 55% 9%)" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(42 22% 86%)" vertical={false} />
                            <XAxis dataKey="age" stroke="hsl(150 12% 45%)" tick={{ fontSize: 12, fontFamily: "IBM Plex Mono" }} />
                            <YAxis stroke="hsl(150 12% 45%)" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                            <Tooltip contentStyle={{ background: "white", border: "1px solid #E5E0D5", borderRadius: 8, fontFamily: "IBM Plex Mono", fontSize: 12 }} formatter={(v) => fmtUSD(v, { decimals: 0 })} />
                            <Area type="monotone" dataKey="balance" stroke="hsl(150 55% 9%)" fill="url(#gRet)" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                    </div>
                    <figcaption className="sr-only">
                        Starting balance {fmtUSD(toNum(current))} grows to {fmtUSD(result.fv, { decimals: 0 })} by age {toNum(retireAge)}, based on {fmtUSD(result.monthly)} monthly contributions and {toNum(returnRate)}% annual return.
                    </figcaption>
                </figure>
            </div>
        </CalculatorShell>
    );
}
